/**
 * Japanese text helpers aligned with R2 `public/orgjs/common.js` (`mb_convert_kana`, kana maps).
 * Adds PHP `S`/`s` space normalization (half-width space ↔ 　) used with `SANK` / `SCK` on name/kana fields.
 */

// ==================== Character Conversion Maps ====================

/**
 * Half-width to Full-width Katakana Map
 */
const HALF_TO_FULL_KANA: Record<string, string> = {
  ｱ: 'ア',
  ｲ: 'イ',
  ｳ: 'ウ',
  ｴ: 'エ',
  ｵ: 'オ',
  ｶ: 'カ',
  ｷ: 'キ',
  ｸ: 'ク',
  ｹ: 'ケ',
  ｺ: 'コ',
  ｻ: 'サ',
  ｼ: 'シ',
  ｽ: 'ス',
  ｾ: 'セ',
  ｿ: 'ソ',
  ﾀ: 'タ',
  ﾁ: 'チ',
  ﾂ: 'ツ',
  ﾃ: 'テ',
  ﾄ: 'ト',
  ﾅ: 'ナ',
  ﾆ: 'ニ',
  ﾇ: 'ヌ',
  ﾈ: 'ネ',
  ﾉ: 'ノ',
  ﾊ: 'ハ',
  ﾋ: 'ヒ',
  ﾌ: 'フ',
  ﾍ: 'ヘ',
  ﾎ: 'ホ',
  ﾏ: 'マ',
  ﾐ: 'ミ',
  ﾑ: 'ム',
  ﾒ: 'メ',
  ﾓ: 'モ',
  ﾔ: 'ヤ',
  ﾕ: 'ユ',
  ﾖ: 'ヨ',
  ﾗ: 'ラ',
  ﾘ: 'リ',
  ﾙ: 'ル',
  ﾚ: 'レ',
  ﾛ: 'ロ',
  ﾜ: 'ワ',
  ｦ: 'ヲ',
  ﾝ: 'ン',
  ｧ: 'ァ',
  ｨ: 'ィ',
  ｩ: 'ゥ',
  ｪ: 'ェ',
  ｫ: 'ォ',
  ｬ: 'ャ',
  ｭ: 'ュ',
  ｮ: 'ョ',
  ｯ: 'ッ',
  ﾞ: '゛',
  ﾟ: '゜',
  ｰ: 'ー',
  '｡': '。',
  '｢': '「',
  '｣': '」',
  '､': '、',
  '･': '・',
};

/**
 * Voiced sound marks handling (ﾞ dakuten, ﾟ handakuten)
 */
const VOICED_KANA: Record<string, string> = {
  カ: 'ガ',
  キ: 'ギ',
  ク: 'グ',
  ケ: 'ゲ',
  コ: 'ゴ',
  サ: 'ザ',
  シ: 'ジ',
  ス: 'ズ',
  セ: 'ゼ',
  ソ: 'ゾ',
  タ: 'ダ',
  チ: 'ヂ',
  ツ: 'ヅ',
  テ: 'デ',
  ト: 'ド',
  ハ: 'バ',
  ヒ: 'ビ',
  フ: 'ブ',
  ヘ: 'ベ',
  ホ: 'ボ',
  ウ: 'ヴ',
};

const SEMI_VOICED_KANA: Record<string, string> = {
  ハ: 'パ',
  ヒ: 'ピ',
  フ: 'プ',
  ヘ: 'ペ',
  ホ: 'ポ',
};

/** Inverse of R2 `hankana2zenkana` mode 2 for full-width katakana → half-width (mode `k`). */
const FULL_WIDTH_KATAKANA_TO_HALF: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const [half, full] of Object.entries(HALF_TO_FULL_KANA)) {
    if (half === 'ﾞ' || half === 'ﾟ') continue;
    if (full === '゛' || full === '゜') continue;
    m[full] = half;
  }
  for (const [base, voiced] of Object.entries(VOICED_KANA)) {
    const hb = m[base];
    if (hb) m[voiced] = hb + 'ﾞ';
  }
  for (const [base, semi] of Object.entries(SEMI_VOICED_KANA)) {
    const hb = m[base];
    if (hb) m[semi] = hb + 'ﾟ';
  }
  return m;
})();

/**
 * Hiragana to Katakana offset (0x3041-0x3096 is hiragana range)
 */
const KATAKANA_OFFSET = 0x60;

// ==================== Character Conversion Functions ====================

/**
 * Convert hiragana to katakana
 */
export function hiraganaToKatakana(str: string): string {
  return str.replace(/[\u3041-\u3096]/g, char =>
    String.fromCharCode(char.charCodeAt(0) + KATAKANA_OFFSET)
  );
}

/**
 * Convert katakana to hiragana
 */
export function katakanaToHiragana(str: string): string {
  return str.replace(/[\u30A1-\u30F6]/g, char =>
    String.fromCharCode(char.charCodeAt(0) - KATAKANA_OFFSET)
  );
}

/**
 * Convert half-width katakana to full-width katakana
 */
export function halfWidthToFullWidthKana(str: string): string {
  let result = '';
  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    const nextChar = str[i + 1];

    // Check for voiced/semi-voiced sound marks
    if (HALF_TO_FULL_KANA[char]) {
      let fullChar = HALF_TO_FULL_KANA[char];

      if (nextChar === 'ﾞ' && VOICED_KANA[fullChar]) {
        fullChar = VOICED_KANA[fullChar];
        i++; // Skip the dakuten
      } else if (nextChar === 'ﾟ' && SEMI_VOICED_KANA[fullChar]) {
        fullChar = SEMI_VOICED_KANA[fullChar];
        i++; // Skip the handakuten
      }

      result += fullChar;
    } else {
      result += char;
    }
  }
  return result;
}

function fullWidthKatakanaToHalfWidth(str: string): string {
  let out = '';
  for (const c of str) {
    out += FULL_WIDTH_KATAKANA_TO_HALF[c] ?? c;
  }
  return out;
}

/**
 * Convert full-width alphanumeric to half-width
 * Equivalent to PHP's mb_convert_kana with "a" option
 */
export function fullWidthToHalfWidthAlphaNum(str: string): string {
  return str.replace(/[Ａ-Ｚａ-ｚ０-９]/g, char =>
    String.fromCharCode(char.charCodeAt(0) - 0xfee0)
  );
}

/**
 * Convert half-width alphanumeric to full-width
 * Equivalent to PHP's mb_convert_kana with "A" option
 */
export function halfWidthToFullWidthAlphaNum(str: string): string {
  return str.replace(/[A-Za-z0-9]/g, char => String.fromCharCode(char.charCodeAt(0) + 0xfee0));
}

/**
 * Convert full-width numbers to half-width
 * Equivalent to PHP's mb_convert_kana with "n" option
 */
export function fullWidthToHalfWidthNumber(str: string): string {
  return str.replace(/[０-９]/g, char => String.fromCharCode(char.charCodeAt(0) - 0xfee0));
}

/**
 * Normalize postal code input: full-width to half-width, remove spaces, max 7 digits.
 * For use in postal code fields (郵便番号) across forms.
 */
export function normalizePostalCodeInput(value: string): string {
  const half = fullWidthToHalfWidthNumber(value);
  return half.replace(/[\s\u3000]/g, '').slice(0, 7);
}

/**
 * Normalize email input.
 *
 * PHP `mb_convert_kana` with `'a'` converts the entire full-width ASCII block
 * (U+FF01..U+FF5E) to half-width \u2014 including symbols like \uff20 \uff0e \uff0d \uff3f. The
 * narrower `mbConvertKana(..., 'a')` port in this file only handles letters
 * and digits, so emails typed in IME full-width mode end up like
 * `test\uff20example\uff0ecom` and fail Zod `.email()`. This helper covers the full
 * range and lowercases \u2014 use it on email blur instead of `mbConvertKana`.
 */
export function normalizeEmailInput(value: string): string {
  return value
    .trim()
    .replace(/[\uff01-\uff5e]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .toLowerCase();
}

/**
 * R2 `mb_convert_kana` (orgjs/common.js): trim, empty option → `A`, multi-char options applied in order.
 * Also `S`/`s` (PHP): half-width space ↔ 　.
 */
export function mbConvertKana(str: string, option: string): string {
  let s = str.trim();
  if (option === '') {
    return mbConvertKana(s, 'A');
  }
  const chars = Array.from(option);
  if (chars.length > 1) {
    let result = s;
    for (let i = 0; i < chars.length; i++) {
      result = mbConvertKana(result, chars[i]);
    }
    return result;
  }

  const op = chars[0];
  /** PHP `mb_convert_kana` `S`: ASCII space → ideographic space (for name/address/kana fields). */
  if (op === 'S') {
    return s.replace(/ /g, '\u3000');
  }
  if (op === 's') {
    return s.replace(/\u3000/g, ' ');
  }
  if (op === 'A') {
    return s.replace(/[A-Za-z0-9]/g, x => String.fromCharCode(x.charCodeAt(0) + 65248));
  }
  if (op === 'a') {
    return s.replace(/[Ａ-Ｚａ-ｚ０-９]/g, x => String.fromCharCode(x.charCodeAt(0) - 65248));
  }
  if (op === 'N') {
    return s.replace(/[0-9]/g, x => String.fromCharCode(x.charCodeAt(0) + 65248));
  }
  if (op === 'n') {
    return s.replace(/[０-９]/g, x => String.fromCharCode(x.charCodeAt(0) - 65248));
  }
  if (op === 'K') {
    return halfWidthToFullWidthKana(s);
  }
  if (op === 'k') {
    return fullWidthKatakanaToHalfWidth(s);
  }
  if (op === 'C') {
    return hiraganaToKatakana(s);
  }
  if (op === 'c') {
    return katakanaToHiragana(s);
  }
  return s;
}

/**
 * Name/kanji field blur.
 * @param kanaOption - `'SANK'` (default): spaces→全角 + half→full alphanumeric/numbers/kana.
 *                     `'SK'`: spaces→全角 + half→full kana only (no alphanumeric conversion).
 */
export function normalizeJapaneseNameOnBlur(raw: string, maxLength = 255, kanaOption: string = 'SANK'): string {
  const normalized = mbConvertKana(raw, kanaOption);
  return normalized.length <= maxLength ? normalized : normalized.slice(0, maxLength);
}

/** Kana field: strip kanji, then `SCK` (spaces → 　, then R2 `CK`). */
export function ensureKatakanaOnly(value: string): string {
  const withoutKanji = value.replace(/[\u4E00-\u9FFF\u3400-\u4DBF]/g, '');
  return mbConvertKana(withoutKanji, 'SCK');
}

// ==================== Name Processing Utilities ====================

/**
 * Extract family name (first part before space)
 * Used when relationship is "家族" to only show family name
 */
export function extractFamilyName(fullName: string): string {
  // Normalize spaces (full-width to half-width)
  const normalized = fullName.replace(/　/g, ' ').trim();
  const parts = normalized.split(' ');
  return parts[0] || '';
}

/** Patient カナ blur — `SCK` (spaces + R2 `CK`). */
export function normalizeNameKana(str: string): string {
  return mbConvertKana(str, 'SCK');
}

/**
 * Normalize phone number
 * - Convert full-width numbers to half-width
 */
export function normalizePhoneNumber(str: string): string {
  return fullWidthToHalfWidthNumber(str);
}

/** Regex: only digits and "-" (電話番号/FAX format) */
export const TELEPHONE_FAX_FORMAT_REGEX = /^[0-9\-]+$/;

/**
 * Normalize 電話番号/FAX input: full-width -> half-width, keep only digits and "-", limit length.
 * @param maxLength Default is 13.
 */
export function normalizeTelephoneOrFaxInput(raw: string, maxLength = 13): string {
  const fullWidthHyphen = '\uFF0D';
  let s = fullWidthToHalfWidthNumber(raw.replace(new RegExp(fullWidthHyphen, 'g'), '-'));
  s = s.replace(/[^0-9-]/g, '');
  return s.slice(0, maxLength);
}

/**
 * Normalize postal code
 * - Convert full-width numbers to half-width
 */
export function normalizePostalCode(str: string): string {
  return fullWidthToHalfWidthNumber(str);
}

/**
 * Normalize insurer number
 * - Convert full-width numbers to half-width
 * - Pad to 8 digits with leading zeros
 */
export function normalizeInsurerNumber(str: string): string {
  const normalized = fullWidthToHalfWidthNumber(str);
  if (normalized && /^\d+$/.test(normalized)) {
    return normalized.padStart(8, '0');
  }
  return normalized;
}

/** Address blur: `SANK` (spaces + R2 `ANK`). */
export function normalizeAddress(str: string): string {
  return mbConvertKana(str, 'SANK');
}

/** R2 `ANK` for card mark/number style fields. */
export function normalizeCardNumber(str: string): string {
  return mbConvertKana(str, 'ANK');
}

/**
 * 記号・番号・公費番号などコード系: trim し、全角英数字 → 半角。
 * `normalizeAddress` / `normalizeCardNumber`（SANK/ANK）は半角 → 全角のためコード欄に使わない。
 */
export function normalizeHalfWidthCodeField(str: string): string {
  return fullWidthToHalfWidthAlphaNum(fullWidthToHalfWidthNumber((str || '').trim()));
}

/**
 * Validate if string contains only valid Katakana characters and spaces
 * Valid characters include full-width katakana, spaces, and punctuation marks
 */
export function isValidKatakana(str: string): boolean {
  if (!str) return false;
  // Match full-width katakana (ァ-ヴ), spaces, and common Japanese punctuation
  const katakanaRegex = /^[\u30A0-\u30FF\u3099-\u309C\s　・ー（）()「」『』ゎ]+$/;
  return katakanaRegex.test(str);
}

export default {
  mbConvertKana,
  hiraganaToKatakana,
  katakanaToHiragana,
  halfWidthToFullWidthKana,
  fullWidthToHalfWidthAlphaNum,
  fullWidthToHalfWidthNumber,
  normalizePostalCodeInput,
  normalizeNameKana,
  normalizePhoneNumber,
  normalizeTelephoneOrFaxInput,
  TELEPHONE_FAX_FORMAT_REGEX,
  normalizePostalCode,
  normalizeInsurerNumber,
  normalizeAddress,
  normalizeCardNumber,
  normalizeHalfWidthCodeField,
  extractFamilyName,
  isValidKatakana,
};
